import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Save, AlertCircle, ExternalLink, Smartphone, Trash2 } from "lucide-react";
import { adminGetTidio, adminPutTidio, adminDeleteTidio } from "../../lib/api";

/**
 * Admin → Tidio Live Chat.
 *
 * One-time setup screen — paste either the full Tidio <script> tag from
 * Tidio dashboard (Settings → Live Chat → Installation) OR just the
 * project key. The backend normalises both to a project key, the public
 * `<TidioLoader />` then injects the official Tidio script on every page.
 *
 * Operator never touches code to change Tidio accounts or providers.
 */
export default function TidioSettingsTab({ pw }) {
  const [snippet, setSnippet] = useState("");
  const [projectKey, setProjectKey] = useState("");
  const [savedKey, setSavedKey] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetTidio(pw).then((r) => {
      setProjectKey(r.project_key || "");
      setSnippet(r.snippet || "");
      setSavedKey(r.project_key || r.derived_key || "");
      setUpdatedAt(r.updated_at);
    }).catch(() => {});
  }, [pw]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await adminPutTidio(pw, { project_key: projectKey.trim(), snippet });
      setSavedKey(r.project_key);
      toast.success("Tidio config saved — reload any open store tab to load the widget.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Disable Tidio? The chat widget will stop appearing on the website until you re-enter a key.")) return;
    try {
      await adminDeleteTidio(pw);
      setProjectKey(""); setSnippet(""); setSavedKey("");
      toast.success("Tidio disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disable");
    }
  };

  return (
    <div className="space-y-6" data-testid="tidio-settings-tab">
      <div>
        <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
          <MessageCircle size={22} className="text-[var(--tc-red)]" /> Tidio Live Chat
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-1">
          Customer-facing chat is powered by Tidio. Paste your Tidio install snippet or project key once, and the chat widget will appear on every page of the website.
        </p>
      </div>

      {/* Status */}
      <div className={`border p-4 ${savedKey ? "border-emerald-700 bg-emerald-950/30" : "border-amber-700 bg-amber-950/30"}`}>
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className={savedKey ? "text-emerald-400 mt-0.5" : "text-amber-400 mt-0.5"} />
          <div className="flex-1">
            <div className={`font-mono text-xs tracking-widest ${savedKey ? "text-emerald-200" : "text-amber-200"}`}>
              STATUS: {savedKey ? "TIDIO IS LIVE" : "NOT CONFIGURED YET"}
            </div>
            {savedKey ? (
              <div className="text-xs text-zinc-400 mt-1">
                Project key <code className="text-zinc-200" data-testid="tidio-saved-key">{savedKey.slice(0, 8)}…</code>
                {updatedAt && <> · last saved {new Date(updatedAt).toLocaleString()}</>}
              </div>
            ) : (
              <div className="text-xs text-zinc-400 mt-1">No Tidio key yet — paste the snippet below to activate the chat widget.</div>
            )}
          </div>
        </div>
      </div>

      {/* Editor */}
      <form onSubmit={handleSave} className="space-y-3 border border-[#222] bg-[#0a0a0a] p-5">
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">
            OPTION A — PASTE FULL TIDIO &lt;SCRIPT&gt; SNIPPET
          </span>
          <textarea
            value={snippet}
            onChange={(e) => setSnippet(e.target.value)}
            data-testid="tidio-snippet-input"
            rows={4}
            placeholder={'<script src="//code.tidio.co/your-project-key.js" async></script>'}
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-xs text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
        </label>
        <div className="flex items-center gap-3 font-mono text-[10px] tracking-widest text-zinc-500">
          <div className="flex-1 h-px bg-[#222]" />
          OR
          <div className="flex-1 h-px bg-[#222]" />
        </div>
        <label className="block">
          <span className="font-mono text-[10px] tracking-widest text-zinc-400">
            OPTION B — PASTE PROJECT KEY ONLY
          </span>
          <input
            type="text"
            value={projectKey}
            onChange={(e) => setProjectKey(e.target.value)}
            data-testid="tidio-key-input"
            placeholder="e.g. kqxxxxxxxxxxxx"
            className="mt-1 w-full bg-black border border-[#333] px-3 py-2 font-mono text-sm text-zinc-100 placeholder-zinc-600 focus:border-[var(--tc-red)] focus:outline-none"
          />
        </label>
        <div className="flex gap-2 pt-1">
          <button
            type="submit" disabled={saving}
            data-testid="tidio-save-btn"
            className="bg-[var(--tc-red)] text-white px-5 py-2 font-mono text-xs tracking-widest hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Save size={14} /> {saving ? "SAVING…" : "SAVE TIDIO CONFIG"}
          </button>
          {savedKey && (
            <button
              type="button" onClick={handleDelete}
              data-testid="tidio-delete-btn"
              className="border border-red-900 hover:bg-red-950 text-red-300 px-4 py-2 font-mono text-xs tracking-widest flex items-center gap-2"
            >
              <Trash2 size={14} /> DISABLE TIDIO
            </button>
          )}
        </div>
      </form>

      {/* Setup instructions */}
      <div className="border border-[#222] bg-[#0a0a0a] p-5 space-y-4">
        <h3 className="font-display text-lg uppercase tracking-tight">Where to find your Tidio code</h3>
        <ol className="list-decimal pl-5 space-y-2 text-sm text-zinc-300">
          <li>Log into your Tidio account at <a href="https://www.tidio.com" target="_blank" rel="noreferrer" className="text-[var(--tc-red)] hover:underline inline-flex items-center gap-1">tidio.com <ExternalLink size={12} /></a>.</li>
          <li>Go to <strong className="text-zinc-100">Settings → Live Chat → Installation</strong>.</li>
          <li>Copy the JavaScript snippet shown (or just the key inside <code className="text-zinc-200">//code.tidio.co/XXXXXX.js</code>).</li>
          <li>Paste it above and hit <strong>SAVE TIDIO CONFIG</strong>. The widget will appear on every public page on the next reload.</li>
        </ol>

        <div className="border-t border-[#222] pt-4 space-y-3">
          <h4 className="font-mono text-xs tracking-widest text-zinc-400">CONFIGURE PRE-CHAT FORM INSIDE TIDIO (not here)</h4>
          <p className="text-sm text-zinc-300">
            Tidio handles the customer&apos;s pre-chat form, automation, and inbox routing. In your Tidio dashboard, go to <strong className="text-zinc-100">Settings → Chat behavior → Pre-chat survey</strong> and enable:
          </p>
          <ul className="list-disc pl-5 text-sm text-zinc-300 space-y-1">
            <li>Name field</li>
            <li>Phone number — <strong>required</strong></li>
            <li>Email address — <strong>required</strong></li>
            <li>Initial message</li>
          </ul>
        </div>

        <div className="border-t border-[#222] pt-4 space-y-2">
          <h4 className="font-mono text-xs tracking-widest text-zinc-400 flex items-center gap-2">
            <Smartphone size={12} /> MOBILE NOTIFICATIONS
          </h4>
          <p className="text-sm text-zinc-300">
            Install the <strong className="text-zinc-100">Tidio mobile app</strong> (App Store / Google Play) and log in with the same Tidio account. You&apos;ll get push notifications for new customer chats wherever you are — no SMS forwarding needed.
          </p>
        </div>
      </div>
    </div>
  );
}
